import { getCountries, type CountryCode } from 'libphonenumber-js';

const VALID_CODES = new Set<string>(getCountries());

function regionLabel(code: string, locale: string): string {
  const tag = locale === 'az' ? 'az' : locale.slice(0, 2);
  try {
    return new Intl.DisplayNames([tag], { type: 'region' }).of(code) ?? code;
  } catch {
    return code;
  }
}

export type CountrySearchRow = { code: string; haystack: string };

function buildSearchRows(): CountrySearchRow[] {
  const rows: CountrySearchRow[] = [];
  for (const code of VALID_CODES) {
    const haystack = [
      code.toLowerCase(),
      regionLabel(code, 'en').toLowerCase(),
      regionLabel(code, 'ru').toLowerCase(),
      regionLabel(code, 'az').toLowerCase(),
    ].join(' ');
    rows.push({ code, haystack });
  }
  rows.sort((a, b) => regionLabel(a.code, 'en').localeCompare(regionLabel(b.code, 'en'), 'en'));
  return rows;
}

/** Статический индекс для поиска по названиям en / ru / az и по коду. */
export const COUNTRY_SEARCH_ROWS: CountrySearchRow[] = buildSearchRows();

export function isPickerCountryCode(raw: string): boolean {
  const c = raw.trim().toUpperCase();
  return c.length === 2 && VALID_CODES.has(c as CountryCode);
}

export function getCountryLabel(regionCode: string, locale: string): string {
  const code = regionCode.trim().toUpperCase();
  if (code.length !== 2 || !VALID_CODES.has(code as CountryCode)) return regionCode;
  return regionLabel(code, locale);
}

export function filterCountrySearchRows(query: string, sortLocale: string): CountrySearchRow[] {
  const q = query.trim().toLowerCase();
  const matched = !q ? COUNTRY_SEARCH_ROWS : COUNTRY_SEARCH_ROWS.filter((r) => r.haystack.includes(q));
  const tag = sortLocale.slice(0, 2);
  return [...matched].sort((a, b) =>
    getCountryLabel(a.code, tag).localeCompare(getCountryLabel(b.code, tag), tag),
  );
}

/** Сопоставляет ввод (код или название на az/ru/en) с ISO-кодом региона из списка телефонии. */
export function normalizeCountryToIso(input: string): string | undefined {
  const t = input.trim();
  if (!t) return undefined;
  const upper = t.toUpperCase();
  if (upper.length === 2 && VALID_CODES.has(upper as CountryCode)) return upper;
  const lower = t.toLowerCase();
  for (const code of VALID_CODES) {
    if (
      regionLabel(code, 'en').toLowerCase() === lower ||
      regionLabel(code, 'ru').toLowerCase() === lower ||
      regionLabel(code, 'az').toLowerCase() === lower
    ) {
      return code;
    }
  }
  return undefined;
}
